import { useTranslation } from "react-i18next";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import * as turf from "@turf/turf";
import ToastError from "../Components/ToastError";
import { useDispatch } from "react-redux";
import { controlService } from "../../Services/control.service";
import { vehicleGroupService } from "../../Services/vehicle.group.service";
import { ToastComponent } from "../Components/ToastComponent";
import { Filters } from "../../components/Filters";
import { useSelected } from "../../contexts/SelectedContext";
import { FaLink, FaMapMarkerAlt } from "react-icons/fa";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Inject,
  Edit,
} from "@syncfusion/ej2-react-grids";
import { FaEdit, FaTrash } from "react-icons/fa";
import { MdFence } from "react-icons/md";
import { HiOutlineRectangleGroup } from "react-icons/hi2";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { getLocationCoordinates } from "../../Services/locations.service";

export const EletronicFences = () => {
  const { t } = useTranslation();
  const pageTitle = t("ELETRONIC_FENCES.TITLE");

  const dispatch = useDispatch();
  dispatch({ type: "title", value: pageTitle });

  const [data, setData] = useState([]);
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(10);

  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const latestPolyline = useRef(null);
  const isLoadingEditDataRef = useRef(false); // Flag para evitar carregamento múltiplo
  const loadedEditItemIdRef = useRef(null); // Rastreia qual item já foi carregado no mapa
  const isUsingNewFlowRef = useRef(false); // Flag para indicar que estamos usando o novo fluxo (tabela)

  const [bufferDistance, setBufferDistance] = useState(500);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [activeTab, setActiveTab] = useState("grid");

  const originMarker = useRef(null);
  const destinationMarker = useRef(null);
  const directionsService = useRef(null);
  const directionsRenderer = useRef(null);
  const geocoder = useRef(null);
  const bufferPolygon = useRef(null);
  const latestDirections = useRef(null);
  const latestBufferGeoJSON = useRef(null);
  const modeRef = useRef("origin");
  const [showModalControl, setShowModalControl] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const gridRef = useRef(null);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [fenceToDelete, setFenceToDelete] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [markerDescription, setMarkerDescription] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);
  const [savingFence, setSavingFence] = useState(false);

  // Referências para autocomplete
  const autocompleteOrigin = useRef(null);
  const autocompleteDestination = useRef(null);
  const originInputRef = useRef(null);
  const destinationInputRef = useRef(null);

  const [showToast, setShowToast] = useState(false);
  const [toastParams, setToastParams] = useState("");

  const [selectedBindMarker, setSelectedBindMarker] = useState(null);
  const [bindList, setBindList] = useState([]);
  const [vehicleGroups, setVehicleGroups] = useState([]);
  const [selectedVehicleIds, setSelectedVehicleIds] = useState([]); // Array de IDs selecionados

  // SelectedContext para o filtro de grupos
  const { selectedValues } = useSelected();
  const selectedGroup = selectedValues.gruposModal?.[0] || 0;

  // Utilitários básicos
  const getSelectedClickMode = () => modeRef.current;

  // Precisão: 6 casas (para retornos/exports)
  const round6 = (n) => Number(n.toFixed(6));
  const roundLL = ({ lat, lng }) => ({ lat: round6(lat), lng: round6(lng) });

  const roundLngLatPairs6 = (coords) =>
    coords.map(([lng, lat]) => [round6(lng), round6(lat)]);

  // Função para plotar rota a partir do areaPath
  const plotRouteFromAreaPath = (areaPathString, bufferDist = null) => {
    if (!areaPathString || areaPathString === "" || !mapInstance.current) {
      console.log("areaPath vazio ou mapa não disponível");
      return;
    }

    try {
      // Parse do JSON do areaPath
      const pathPoints = typeof areaPathString === 'string'
        ? JSON.parse(areaPathString)
        : areaPathString;

      if (!Array.isArray(pathPoints) || pathPoints.length < 2) {
        console.warn("areaPath inválido ou insuficiente:", pathPoints);
        return;
      }

      // Converte os pontos para LatLng do Google Maps
      const path = pathPoints.map(point =>
        new window.google.maps.LatLng(point.lat, point.lng)
      );

      // Cria um objeto DirectionsResult simulado para manter compatibilidade
      // Isso permite que o buffer seja recalculado se necessário
      // Precisamos criar steps que tenham a estrutura correta para buildCenterlineLatLngsFromSteps
      const mockSteps = [];

      // Converte todos os pontos para LatLng
      const latLngs = pathPoints.map(point =>
        new window.google.maps.LatLng(point.lat, point.lng)
      );

      // Cria um step único com todos os pontos codificados
      // Isso é mais eficiente e mantém a estrutura correta
      try {
        const encodedPath = window.google.maps.geometry.encoding.encodePath(latLngs);
        mockSteps.push({
          polyline: {
            points: encodedPath
          }
        });
      } catch (e) {
        console.warn("Erro ao codificar path completo, tentando por segmentos:", e);
        // Fallback: cria steps individuais se a codificação completa falhar
        for (let i = 1; i < pathPoints.length; i++) {
          const prevPoint = pathPoints[i - 1];
          const currentPoint = pathPoints[i];
          try {
            const encodedPath = window.google.maps.geometry.encoding.encodePath([
              new window.google.maps.LatLng(prevPoint.lat, prevPoint.lng),
              new window.google.maps.LatLng(currentPoint.lat, currentPoint.lng)
            ]);
            mockSteps.push({
              polyline: {
                points: encodedPath
              }
            });
          } catch (err) {
            console.warn("Erro ao codificar step:", err);
          }
        }
      }

      const mockDirectionsResult = {
        routes: [{
          legs: [{
            steps: mockSteps
          }]
        }]
      };

      // Armazena o caminho para uso posterior
      latestDirections.current = mockDirectionsResult;

      console.log("✅ Rota plotada a partir do areaPath:", pathPoints.length, "pontos");

      // Se estiver editando (não em modo visualização), calcula rota real para permitir arrastar
      // O directionsRenderer só permite arrastar rotas calculadas pela API do Directions Service
      if (editingItem && !isViewMode && directionsRenderer.current && directionsService.current) {
        // Limpa a polyline anterior se existir
        if (latestPolyline.current) {
          latestPolyline.current.setMap(null);
          latestPolyline.current = null;
        }

        // Calcula uma rota real usando o Directions Service para permitir arrastar
        // Usa origem e destino dos marcadores atuais
        const originPos = originMarker.current?.getPosition();
        const destPos = destinationMarker.current?.getPosition();

        if (originPos && destPos) {
          directionsRenderer.current.setMap(mapInstance.current);

          directionsService.current.route({
            origin: originPos,
            destination: destPos,
            travelMode: window.google.maps.TravelMode.DRIVING,
          })
            .then((res) => {
              latestDirections.current = res;
              directionsRenderer.current.setDirections(res);
              console.log("✅ Rota real calculada e carregada no directionsRenderer (arrastável)");

              // Aplica o buffer automaticamente após calcular a rota
              const bufferToUse = bufferDist !== null ? bufferDist : (bufferDistance || 500);
              setTimeout(() => {
                buildBufferFromDirections(res, bufferToUse);
              }, 100);
            })
            .catch((err) => {
              console.error("Erro ao calcular rota real:", err);
              // Fallback: usa polyline simples se falhar
              if (latestPolyline.current) {
                latestPolyline.current.setMap(null);
              }
              latestPolyline.current = new window.google.maps.Polyline({
                path: path,
                geodesic: true,
                strokeColor: "#1e40af",
                strokeOpacity: 1.0,
                strokeWeight: 3,
                map: mapInstance.current
              });

              // Aplica buffer com o mockDirectionsResult como fallback
              const bufferToUse = bufferDist !== null ? bufferDist : (bufferDistance || 500);
              setTimeout(() => {
                if (mockSteps.length > 0) {
                  buildBufferFromDirections(mockDirectionsResult, bufferToUse);
                }
              }, 100);
            });

          // Retorna aqui para não executar o código abaixo
          return;
        } else {
          console.warn("Marcadores de origem ou destino não disponíveis para calcular rota real");
        }
      }

      // Modo visualização ou quando não há directionsRenderer: usa polyline simples
      if (latestPolyline.current) {
        latestPolyline.current.setMap(null);
      }

      latestPolyline.current = new window.google.maps.Polyline({
        path: path,
        geodesic: true,
        strokeColor: "#1e40af",
        strokeOpacity: 1.0,
        strokeWeight: 3,
        map: mapInstance.current
      });

      // APLICA O BUFFER AUTOMATICAMENTE (como se tivesse clicado em "Aplicar")
      // Usa o bufferDistance passado como parâmetro ou o valor atual do estado
      const bufferToUse = bufferDist !== null ? bufferDist : (bufferDistance || 500);
      setTimeout(() => {
        if (mockSteps.length > 0) {
          console.log("Aplicando buffer com largura:", bufferToUse);
          // Atualiza o estado para manter consistência
          if (bufferDist !== null) {
            setBufferDistance(bufferDist);
          }
          // Chama buildBufferFromDirections passando o bufferDistance diretamente
          buildBufferFromDirections(mockDirectionsResult, bufferToUse);
        } else {
          console.warn("Não foi possível aplicar buffer: mockSteps=", mockSteps.length);
        }
      }, 100);
    } catch (error) {
      console.error("❌ Erro ao plotar rota do areaPath:", error);
    }
  };

  const handleParamEdit = async (row) => {
    // Marca que estamos usando o novo fluxo
    isUsingNewFlowRef.current = true;

    try {
      // 1. CHAMA O ENDPOINT PARA BUSCAR OS DADOS DO POLÍGONO
      const polygonData = await controlService.findPolygon(row.markerId);

      console.log("Dados do polígono recebidos:", polygonData);

      // Prepara o item para edição, usando os dados do endpoint se disponíveis
      const editable = {
        ...row,
        markerDescription: row.markerDescription || "",
        latitude: Number(row.latitude),
        longitude: Number(row.longitude),
        address: row.address || row.location || "",
        area: polygonData?.area || row.area || "NA", // Usa area do endpoint se disponível
        radius: row.radius || 0,
        markerTypeId: 3,
        markerId: row.markerId,
        destinationLatitude: row.destinationLatitude || row.latitude,
        destinationLongitude: row.destinationLongitude || row.longitude,
        destinationAddress: row.destinationAddress || row.address || row.location || "",
        areaPath: polygonData?.areaPath || null, // Armazena o areaPath
      };

      setEditingItem(editable);
      setIsViewMode(false);
      setViewingItem(null);
      setMarkerDescription(editable.markerDescription);
      setActiveTab("map");

      // Aguarda um pouco para garantir que o mapa está carregado
      setTimeout(async () => {
        if (!mapInstance.current) return;

        const map = mapInstance.current;

        // Limpa camadas existentes
        clearLayers();
        clearMarkers();
        directionsRenderer.current.set("directions", null);

        // Limpa a polyline anterior se existir
        if (latestPolyline.current) {
          latestPolyline.current.setMap(null);
          latestPolyline.current = null;
        }

        // 1. ATUALIZA OS ESTADOS DOS INPUTS COM OS DADOS
        setOrigin(row.address || t("ELETRONIC_FENCES.ORIGIN_FENCE"));
        setDestination(
          row.destinationAddress || row.address || t("ELETRONIC_FENCES.DESTINATION_FENCE")
        );

        // 2. ATUALIZA O BUFFER DISTANCE (IMPORTANTE: fazer antes de plotar a rota)
        const currentBufferDistance = row.radius || 500;
        setBufferDistance(currentBufferDistance);

        // 3. CRIA OS MARCADORES DE ORIGEM E DESTINO (arrastáveis em modo edição)
        const originPoint = {
          lat: row.latitude,
          lng: row.longitude
        };

        const destinationPoint = {
          lat: row.destinationLatitude || row.latitude,
          lng: row.destinationLongitude || row.longitude
        };

        // Cria marcador de origem (arrastável em modo edição)
        originMarker.current = new window.google.maps.Marker({
          position: originPoint,
          map: map,
          label: { text: "O", color: "#111827", fontWeight: "700" },
          draggable: true
        });

        // Cria marcador de destino (arrastável em modo edição)
        destinationMarker.current = new window.google.maps.Marker({
          position: destinationPoint,
          map: map,
          label: { text: "D", color: "#111827", fontWeight: "700" },
          draggable: true
        });

        // 4. PLOTA A ROTA A PARTIR DO areaPath SE DISPONÍVEL
        // Se temos areaPath, calcula uma rota real para permitir arrastar (igual ao modo criação)
        const hasAreaPath = polygonData?.areaPath && polygonData.areaPath !== "" && polygonData.areaPath !== "NA";
        const hasArea = polygonData?.area && polygonData.area !== "NA" && polygonData.area !== "";

        if (hasAreaPath) {
          // Aguarda um pequeno delay para garantir que bufferDistance foi atualizado e marcadores estão prontos
          setTimeout(() => {
            // Calcula rota real usando waypoints do areaPath para seguir o caminho salvo e ainda ser arrastável
            if (canRoute() && directionsService.current && directionsRenderer.current) {
              // Limpa qualquer polyline anterior
              if (latestPolyline.current) {
                latestPolyline.current.setMap(null);
                latestPolyline.current = null;
              }

              try {
                // Parse do areaPath para obter os pontos intermediários
                const pathPoints = typeof polygonData.areaPath === 'string'
                  ? JSON.parse(polygonData.areaPath)
                  : polygonData.areaPath;

                if (!Array.isArray(pathPoints) || pathPoints.length < 2) {
                  throw new Error("areaPath inválido");
                }

                // Extrai waypoints intermediários (excluindo primeiro e último que são origem e destino)
                // Se houver muitos pontos, seleciona alguns estratégicos para não exceder o limite da API (máx 25 waypoints)
                const waypoints = [];
                if (pathPoints.length > 2) {
                  // Limite máximo de waypoints da API do Google Maps
                  const maxWaypoints = 25;

                  if (pathPoints.length - 2 <= maxWaypoints) {
                    // Se temos poucos pontos, usa todos os intermediários
                    for (let i = 1; i < pathPoints.length - 1; i++) {
                      waypoints.push({
                        location: new window.google.maps.LatLng(pathPoints[i].lat, pathPoints[i].lng),
                        stopover: false // Não para nos waypoints, apenas passa por eles
                      });
                    }
                  } else {
                    // Se temos muitos pontos, seleciona estratégicos distribuídos uniformemente
                    const step = Math.ceil((pathPoints.length - 2) / maxWaypoints);
                    for (let i = 1; i < pathPoints.length - 1; i += step) {
                      if (waypoints.length >= maxWaypoints) break;
                      waypoints.push({
                        location: new window.google.maps.LatLng(pathPoints[i].lat, pathPoints[i].lng),
                        stopover: false
                      });
                    }
                    // Garante que o último ponto intermediário seja incluído
                    if (waypoints.length < maxWaypoints && pathPoints.length > 2) {
                      const lastIntermediate = pathPoints[pathPoints.length - 2];
                      waypoints.push({
                        location: new window.google.maps.LatLng(lastIntermediate.lat, lastIntermediate.lng),
                        stopover: false
                      });
                    }
                  }
                }

                console.log(`📍 Usando ${waypoints.length} waypoints do areaPath para seguir o caminho salvo`);

                // Calcula a rota real com waypoints para seguir o caminho do areaPath
                directionsRenderer.current.setMap(map);
                const originPos = originMarker.current.getPosition();
                const destPos = destinationMarker.current.getPosition();

                directionsService.current.route({
                  origin: originPos,
                  destination: destPos,
                  waypoints: waypoints, // Usa waypoints do areaPath
                  travelMode: window.google.maps.TravelMode.DRIVING,
                  optimizeWaypoints: false, // Mantém a ordem dos waypoints
                })
                  .then((res) => {
                    latestDirections.current = res;
                    directionsRenderer.current.setDirections(res);
                    console.log("✅ Rota real calculada com waypoints do areaPath (arrastável)");

                    // Aplica o buffer automaticamente com a largura salva
                    setTimeout(() => {
                      buildBufferFromDirections(res, currentBufferDistance);
                    }, 100);
                  })
                  .catch((err) => {
                    console.error("Erro ao calcular rota com waypoints:", err);
                    // Se falhar com waypoints, tenta sem waypoints
                    directionsService.current.route({
                      origin: originPos,
                      destination: destPos,
                      travelMode: window.google.maps.TravelMode.DRIVING,
                    })
                      .then((res) => {
                        latestDirections.current = res;
                        directionsRenderer.current.setDirections(res);
                        console.log("✅ Rota real calculada sem waypoints (arrastável)");

                        setTimeout(() => {
                          buildBufferFromDirections(res, currentBufferDistance);
                        }, 100);
                      })
                      .catch((err2) => {
                        console.error("Erro ao calcular rota na edição:", err2);
                        // Fallback: usa plotRouteFromAreaPath se falhar
                        plotRouteFromAreaPath(polygonData.areaPath, currentBufferDistance);
                      });
                  });
              } catch (parseError) {
                console.error("Erro ao parsear areaPath:", parseError);
                // Se falhar ao parsear, calcula rota simples
                directionsRenderer.current.setMap(map);
                const originPos = originMarker.current.getPosition();
                const destPos = destinationMarker.current.getPosition();

                directionsService.current.route({
                  origin: originPos,
                  destination: destPos,
                  travelMode: window.google.maps.TravelMode.DRIVING,
                })
                  .then((res) => {
                    latestDirections.current = res;
                    directionsRenderer.current.setDirections(res);
                    console.log("✅ Rota real calculada (arrastável)");

                    setTimeout(() => {
                      buildBufferFromDirections(res, currentBufferDistance);
                    }, 100);
                  })
                  .catch((err) => {
                    console.error("Erro ao calcular rota na edição:", err);
                    plotRouteFromAreaPath(polygonData.areaPath, currentBufferDistance);
                  });
              }
            } else {
              // Se não pode calcular rota, usa o método antigo
              console.log("⚠️ Não pode calcular rota, usando plotRouteFromAreaPath");
              plotRouteFromAreaPath(polygonData.areaPath, currentBufferDistance);
            }
          }, 200);
        } else {
          // 5. FALLBACK: Se não temos areaPath, plotamos o polígono a partir do area
          if (hasArea) {
            try {
              // Remove asteriscos e converte para objeto
              const areaString = polygonData.area.replace(/\*/g, '"');
              const coordinates = JSON.parse(areaString);

              if (coordinates && Array.isArray(coordinates) && coordinates.length >= 3) {
                // Converte para formato do Google Maps
                const gCoords = coordinates.map(coord => ({
                  lat: Number(coord.lat),
                  lng: Number(coord.lng)
                }));

                // Usa drawOriginalPolygon para garantir cores e propriedades corretas
                drawOriginalPolygon(gCoords);

                // Adiciona listeners para atualizar o editingItem quando o polígono é editado
                if (bufferPolygon.current) {
                  const updatePolygonCoordinates = () => {
                    const newPaths = bufferPolygon.current.getPaths();
                    const newCoordinates = [];
                    newPaths.forEach(path => {
                      path.forEach(latLng => {
                        newCoordinates.push({
                          lat: round6(latLng.lat()),
                          lng: round6(latLng.lng())
                        });
                      });
                    });
                    setEditingItem(prev => ({
                      ...prev,
                      area: JSON.stringify(newCoordinates).replace(/"/g, "*")
                    }));
                  };

                  bufferPolygon.current.addListener("mouseup", updatePolygonCoordinates);
                  bufferPolygon.current.addListener("dragend", updatePolygonCoordinates);

                  // Centraliza o mapa no polígono
                  const bounds = new window.google.maps.LatLngBounds();
                  gCoords.forEach(coord => {
                    bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
                  });
                  map.fitBounds(bounds);
                }
              }
            } catch (error) {
              console.error("Erro ao plotar polígono do area:", error);
            }
          }
        }

        // 6. FALLBACK: Se não houver areaPath nem area, calcula a rota automaticamente
        if (!hasAreaPath && !hasArea) {
          if (canRoute()) {
            directionsRenderer.current.setMap(map);
            const originPos = originMarker.current.getPosition();
            const destPos = destinationMarker.current.getPosition();

            try {
              const res = await directionsService.current.route({
                origin: originPos,
                destination: destPos,
                travelMode: window.google.maps.TravelMode.DRIVING,
              });

              latestDirections.current = res;
              directionsRenderer.current.setDirections(res);

              // Aplica o buffer automaticamente
              buildBufferFromDirections(res);

              // Reseta a flag após o carregamento automático terminar
              isUsingNewFlowRef.current = false;
            } catch (err) {
              setToastParams(
                JSON.stringify({
                  show: true,
                  title: t("COMMON.ERROR"),
                  body: t("ELETRONIC_FENCES.ERROR_ROUTE_DRAW"),
                  color: "danger",
                })
              );
              setShowToast(true);
              // Reseta a flag mesmo em caso de erro
              isUsingNewFlowRef.current = false;
            }
          } else {
            // Se não pode calcular rota, reseta a flag
            isUsingNewFlowRef.current = false;
          }
        } else {
          // Se já plotou a rota do areaPath ou área, reseta a flag
          isUsingNewFlowRef.current = false;
        }
      }, 500);
    } catch (error) {
      console.error("Erro ao buscar dados do polígono:", error);
      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.ERROR"),
          body: t("ELETRONIC_FENCES.ERROR_LOAD_FENCE_DATA"),
          color: "danger",
        })
      );
      setShowToast(true);
      isUsingNewFlowRef.current = false;
    }
  };

  const handleEditFromView = () => {
    if (viewingItem) {
      setEditingItem(viewingItem);
      setMarkerDescription(viewingItem.markerDescription || "");
      setIsViewMode(false);

      // Aguarda um pouco para garantir que o mapa está carregado
      setTimeout(async () => {
        if (!mapInstance.current) return;

        const map = mapInstance.current;

        // Limpa camadas existentes
        clearLayers();
        clearMarkers();
        directionsRenderer.current.set("directions", null);

        // Limpa a polyline anterior se existir
        if (latestPolyline.current) {
          latestPolyline.current.setMap(null);
          latestPolyline.current = null;
        }

        // 1. ATUALIZA OS ESTADOS DOS INPUTS COM OS DADOS
        setOrigin(viewingItem.address || t("ELETRONIC_FENCES.ORIGIN_FENCE"));
        setDestination(
          viewingItem.destinationAddress ||
            viewingItem.address ||
            t("ELETRONIC_FENCES.DESTINATION_FENCE")
        );

        // 2. ATUALIZA O BUFFER DISTANCE
        if (viewingItem.radius) {
          setBufferDistance(viewingItem.radius);
        }

        // 3. CRIA OS MARCADORES DE ORIGEM E DESTINO (arrastáveis em modo edição)
        const originPoint = {
          lat: viewingItem.latitude,
          lng: viewingItem.longitude
        };

        const destinationPoint = {
          lat: viewingItem.destinationLatitude || viewingItem.latitude,
          lng: viewingItem.destinationLongitude || viewingItem.longitude
        };

        // Cria marcador de origem (arrastável em modo edição)
        originMarker.current = new window.google.maps.Marker({
          position: originPoint,
          map: map,
          label: { text: "O", color: "#111827", fontWeight: "700" },
          draggable: true
        });

        // Cria marcador de destino (arrastável em modo edição)
        destinationMarker.current = new window.google.maps.Marker({
          position: destinationPoint,
          map: map,
          label: { text: "D", color: "#111827", fontWeight: "700" },
          draggable: true
        });

        // 4. CALCULA A ROTA AUTOMATICAMENTE (como se tivesse clicado em "Calcular rota")
        if (canRoute()) {
          directionsRenderer.current.setMap(map);
          const originPos = originMarker.current.getPosition();
          const destPos = destinationMarker.current.getPosition();

          try {
            const res = await directionsService.current.route({
              origin: originPos,
              destination: destPos,
              travelMode: window.google.maps.TravelMode.DRIVING,
            });

            latestDirections.current = res;
            directionsRenderer.current.setDirections(res);

            // 5. APLICA O BUFFER AUTOMATICAMENTE (como se tivesse clicado em "Aplicar")
            buildBufferFromDirections(res);

            // Reseta a flag após o carregamento automático terminar
            isUsingNewFlowRef.current = false;
          } catch (err) {
            setToastParams(
              JSON.stringify({
                show: true,
                title: t("COMMON.ERROR"),
                body: t("ELETRONIC_FENCES.ERROR_ROUTE_DRAW"),
                color: "danger",
              })
            );
            // Reseta a flag mesmo em caso de erro
            isUsingNewFlowRef.current = false;
          }
        } else {
          // Se não pode calcular rota, reseta a flag
          isUsingNewFlowRef.current = false;
        }
      }, 300);
    }
  };

  const openDeleteModal = (fence) => {
    setFenceToDelete(fence);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await controlService.deleteAddress(
        fenceToDelete.markerId,
        fenceToDelete.markerTypeId
      );
      if (response?.status === 400) {
        setToastParams(
          JSON.stringify({
            show: true,
            title: response.friendlyMessage,
            body: response.message,
            color: "Warning",
          })
        );
      } else if (response) {
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("COMMON.SUCCESS"),
            body: t("ELETRONIC_FENCES.FENCE_DELETED_SUCCESS"),
            color: "Success",
          })
        );
      } else {
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("COMMON.ERROR"),
            body: t("ELETRONIC_FENCES.FENCE_DELETE_FAIL"),
            color: "Danger",
          })
        );
      }
    } catch (error) {
      console.error("Erro ao excluir a cerca:", error);
      if (error?.response?.status === 400) {
        setToastParams(
          JSON.stringify({
            show: true,
            title: error?.response?.friendlyMessage,
            body: error?.response?.message,
            color: "Warning",
          })
        );
      } else {
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("COMMON.ERROR"),
            body: t("ELETRONIC_FENCES.UNEXPECTED_DELETE_FAILURE"),
            color: "Danger",
          })
        );
      }
    } finally {
      // setShowConfirmModal(false);
      // setFenceToDelete(null);
      // setTimeout(async () => {
      //   await fetchData();
      // }, 500);
    }
  };

  // Função para criar marcador de origem/destino
  const setMarker = (type, position, label) => {
    const markerOptions = {
      position,
      map: mapInstance.current,
      label: { text: label, color: "#111827", fontWeight: "700" },
    };

    if (type === "origin") {
      if (originMarker.current) originMarker.current.setMap(null);
      originMarker.current = new window.google.maps.Marker(markerOptions);
    } else {
      if (destinationMarker.current) destinationMarker.current.setMap(null);
      destinationMarker.current = new window.google.maps.Marker(markerOptions);
    }
  };

  // Geocodificação reversa para input
  const reverseGeocodeToInput = (latlng, targetType) => {
    geocoder.current
      .geocode({ location: latlng })
      .then(({ results }) => {
        if (results && results[0]) {
          const address = results[0].formatted_address;
          if (targetType === "origin") setOrigin(address);
          else setDestination(address);
        }
      })
      .catch(() => {
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("COMMON.ERROR"),
            body: t("ELETRONIC_FENCES.REVERSE_GEOCODING_ERROR"),
            color: "danger",
          })
        );
      });
  };

  // Limpar camadas
  const clearLayers = () => {
    if (bufferPolygon.current) {
      bufferPolygon.current.setMap(null);
      bufferPolygon.current = null;
    }
    latestBufferGeoJSON.current = null;
  };

  // Limpar marcadores
  const clearMarkers = () => {
    if (originMarker.current) {
      originMarker.current.setMap(null);
      originMarker.current = null;
    }
    if (destinationMarker.current) {
      destinationMarker.current.setMap(null);
      destinationMarker.current = null;
    }
  };

  // Rota detalhada (todos os steps)
  const getRoutePointsDetailed = (directionsResult) => {
    const pts = [];
    const route = directionsResult.routes[0];
    for (const leg of route.legs) {
      for (const step of leg.steps) {
        const seg = window.google.maps.geometry.encoding.decodePath(
          step.polyline.points
        );
        for (const ll of seg) {
          const cur = { lat: ll.lat(), lng: ll.lng() };
          const last = pts[pts.length - 1];
          if (!last || last.lat !== cur.lat || last.lng !== cur.lng) {
            pts.push(roundLL(cur));
          }
        }
      }
    }
    return pts;
  };

  // Extrai o caminho da rota (areaPath) para enviar no payload
  const getRoutePath = () => {
    // Tenta usar a rota calculada (latestDirections)
    if (latestDirections.current) {
      try {
        const pathPoints = getRoutePointsDetailed(latestDirections.current);
        if (pathPoints && pathPoints.length > 0) {
          return JSON.stringify(pathPoints);
        }
      } catch (error) {
        console.error("Erro ao extrair caminho da rota:", error);
      }
    }

    // Fallback: se não houver rota calculada, usa origem e destino como caminho mínimo
    if (originMarker.current && destinationMarker.current) {
      try {
        const originPos = originMarker.current.getPosition();
        const destPos = destinationMarker.current.getPosition();
        if (originPos && destPos) {
          const fallbackPath = [
            { lat: round6(originPos.lat()), lng: round6(originPos.lng()) },
            { lat: round6(destPos.lat()), lng: round6(destPos.lng()) }
          ];
          return JSON.stringify(fallbackPath);
        }
      } catch (error) {
        console.error("Erro ao criar caminho fallback:", error);
      }
    }

    return null;
  };

  // Eixo de alta precisão: concatena TODOS os pontos de TODOS os steps (LatLng[])
  const buildCenterlineLatLngsFromSteps = (directionsResult) => {
    const latlngs = [];
    const route = directionsResult.routes[0];
    for (const leg of route.legs) {
      for (const step of leg.steps) {
        const seg = window.google.maps.geometry.encoding.decodePath(
          step.polyline.points
        );
        for (const ll of seg) {
          const last = latlngs[latlngs.length - 1];
          if (!last || last.lat() !== ll.lat() || last.lng() !== ll.lng()) {
            latlngs.push(ll);
          }
        }
      }
    }
    return latlngs;
  };

  // Polígono
  const drawOriginalPolygon = (gCoords) => {
    if (bufferPolygon.current) bufferPolygon.current.setMap(null);

    // Sempre cria polígono editável quando não estiver em modo visualização
    // Editável tanto na criação quanto na edição (mas não na visualização)
    const isEditable = !isViewMode; // Editável se não estiver em modo visualização

    bufferPolygon.current = new window.google.maps.Polygon({
      paths: gCoords,
      map: mapInstance.current,
      strokeColor: "#2563eb", // Azul - corresponde à borda do polígono
      strokeOpacity: 0.95,
      strokeWeight: 2,
      fillColor: "#93c5fd", // Azul claro - corresponde à legenda "Cerca Eletronica"
      fillOpacity: 0.35, // Aumenta um pouco a opacidade para melhor visibilidade
      editable: isEditable, // Editável na criação e edição (não na visualização)
      draggable: isEditable, // Arrastável na criação e edição (não na visualização)
      clickable: false,
    });
  };

  // Buffer geodésico (WGS84) usando TODOS os pontos
  const buildBufferFromDirections = (directionsResult, customBufferDistance = null) => {
    // 1) Eixo detalhado (todos os steps)
    const centerLatLngs = buildCenterlineLatLngsFromSteps(directionsResult);
    if (!centerLatLngs.length) return;

    // 2) Converter para GeoJSON [lng,lat], limpar duplicados
    const coords = centerLatLngs
      .map((ll) => [ll.lng(), ll.lat()])
      .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat));

    if (coords.length < 2) return;

    let line = turf.lineString(coords);
    line = turf.cleanCoords(line); // remove vértices duplicados/consecutivos

    // 3) Raio = metade da largura (metros → km)
    // Usa o valor customizado se fornecido, senão usa o estado
    const bufferDistToUse = customBufferDistance !== null ? customBufferDistance : (bufferDistance || 500);
    const radiusKm = Math.max(0.01, bufferDistToUse / 2000);

    // 4) Buffer geodésico direto em WGS84 (sem projetar)
    const buffered = turf.buffer(line, radiusKm, {
      units: "kilometers",
      steps: 128,
    });
    latestBufferGeoJSON.current = buffered;

    // 5) Escolher anel externo (Polygon/MultiPolygon)
    const geom = buffered.geometry;
    let ring;
    if (geom.type === "Polygon") {
      ring = geom.coordinates[0];
    } else if (geom.type === "MultiPolygon") {
      // Fica com o polígono de maior área
      let best = null,
        bestArea = -Infinity;
      for (const poly of geom.coordinates) {
        const r = poly[0];
        const a = turf.area(turf.polygon([r]));
        if (a > bestArea) {
          bestArea = a;
          best = r;
        }
      }
      ring = best;
    } else {
      return;
    }

    // 6) Desenho no Google
    const gCoords = ring.map(([lng, lat]) => ({ lat, lng }));
    drawOriginalPolygon(gCoords);

    // Adiciona listeners para atualizar quando o polígono é editado (tanto na criação quanto na edição)
    // Aguarda um pequeno delay para garantir que o polígono foi criado
    setTimeout(() => {
      if (bufferPolygon.current && !isViewMode) {
        // Remove listeners anteriores se existirem para evitar duplicação
        window.google.maps.event.clearInstanceListeners(bufferPolygon.current);

        const updatePolygonCoordinates = () => {
          if (!bufferPolygon.current) return;
          const newPaths = bufferPolygon.current.getPaths();
          const newCoordinates = [];
          newPaths.forEach(path => {
            path.forEach(latLng => {
              newCoordinates.push({
                lat: round6(latLng.lat()),
                lng: round6(latLng.lng())
              });
            });
          });

          // Se estiver em modo edição, atualiza o editingItem
          if (editingItem) {
            setEditingItem(prev => ({
              ...prev,
              area: JSON.stringify(newCoordinates).replace(/"/g, "*")
            }));
          }
          // Se estiver em modo criação, atualiza o latestBufferGeoJSON para salvar depois
          // O latestBufferGeoJSON já é atualizado quando o buffer é criado
        };

        bufferPolygon.current.addListener("mouseup", updatePolygonCoordinates);
        bufferPolygon.current.addListener("dragend", updatePolygonCoordinates);
      }
    }, 50);

    const bounds = new window.google.maps.LatLngBounds();
    gCoords.forEach((p) => bounds.extend(p));
    mapInstance.current.fitBounds(bounds);
  };

  // Roteamento
  const canRoute = () => {
    const originPos = originMarker.current?.getPosition();
    const destPos = destinationMarker.current?.getPosition();
    return !!(originPos && destPos);
  };

  const routeNow = () => {
    if (!canRoute()) return;

    // Se estiver em modo edição, limpa a polyline tracejada antes de calcular a rota
    if (editingItem && latestPolyline.current) {
      latestPolyline.current.setMap(null);
      latestPolyline.current = null;
    }

    // Se estiver em modo edição e houver polígono antigo, remove para criar um novo baseado na rota
    if (editingItem && bufferPolygon.current) {
      bufferPolygon.current.setMap(null);
      bufferPolygon.current = null;
    }

    directionsRenderer.current.setMap(mapInstance.current);
    const originPos = originMarker.current.getPosition();
    const destPos = destinationMarker.current.getPosition();

    directionsService.current
      .route({
        origin: originPos,
        destination: destPos,
        travelMode: window.google.maps.TravelMode.DRIVING,
      })
      .then((res) => {
        latestDirections.current = res;
        directionsRenderer.current.setDirections(res);
        // Não cria o polígono automaticamente - apenas mostra a rota
        // O usuário deve clicar em "Aplicar" para criar o polígono
      })
      .catch((err) => {
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("COMMON.ERROR"),
            body: t("ELETRONIC_FENCES.ERROR_ROUTE_DRAW"),
            color: "danger",
          })
        );
        console.error("Erro ao traçar rota:", err);
        setShowToast(true);
      });
  };

  // Função de download

  // Limpa dados quando muda da aba Cercas para Mapa e não está editando
  const prevActiveTabRef = useRef(activeTab);
  useEffect(() => {
    // Só limpa se mudou de "grid" para "map" e não está editando
    if (
      prevActiveTabRef.current === "grid" &&
      activeTab === "map" &&
      !editingItem &&
      !params.id
    ) {
      // Limpa tudo quando vai para o mapa sem estar editando (modo criação)
      clearLayers();
      clearMarkers();
      if (directionsRenderer.current) {
        directionsRenderer.current.set("directions", null);
      }

      // Limpa a polyline tracejada
      if (latestPolyline.current) {
        latestPolyline.current.setMap(null);
        latestPolyline.current = null;
      }

      setOrigin("");
      setDestination("");
      setBufferDistance(500);
      setMarkerDescription("");
      latestDirections.current = null;
      latestBufferGeoJSON.current = null;
    }

    // Atualiza a referência da aba anterior
    prevActiveTabRef.current = activeTab;
  }, [activeTab, editingItem, params.id]);

  useEffect(() => {
    if (activeTab == "map") {
      // Verifica se o Google Maps está disponível
      if (!window.google || !window.google.maps) {
        console.error("Google Maps não está disponível");
        return;
      }

      // Função para inicializar o mapa
      const initMap = () => {
        // Verifica se o elemento do mapa existe
        if (!mapRef.current) {
          // Tenta novamente após um pequeno delay
          setTimeout(initMap, 100);
          return;
        }

        // Verifica se o mapa já existe para não recriar
        if (mapInstance.current) {
          // Força redimensionamento do mapa
          setTimeout(() => {
            if (mapInstance.current) {
              window.google.maps.event.trigger(mapInstance.current, 'resize');
            }
          }, 100);

          // Se estiver editando e ainda não carregou este item, carrega os dados
          // Verifica também se não está no meio de um carregamento e se não está usando o novo fluxo
          if (editingItem &&
            loadedEditItemIdRef.current !== editingItem.markerId &&
            !isLoadingEditDataRef.current &&
            !isUsingNewFlowRef.current) {
            setTimeout(() => {
              loadEditingData(editingItem);
            }, 200);
            return;
          }
        }

        // Cria o mapa
        const map = new window.google.maps.Map(mapRef.current, {
          center: { lat: -23.55, lng: -46.63 },
          zoom: 12,
        });
        mapInstance.current = map;

        geocoder.current = new window.google.maps.Geocoder();
        directionsService.current = new window.google.maps.DirectionsService();
        directionsRenderer.current = new window.google.maps.DirectionsRenderer({
          draggable: true,
          map,
          suppressMarkers: true, // Desabilita marcadores automáticos para usar nossos próprios marcadores
        });

        // Clique no mapa
        map.addListener("click", (e) => {
          const mode = getSelectedClickMode();
          setMarker(mode, e.latLng, mode === "origin" ? "O" : "D");
          reverseGeocodeToInput(e.latLng, mode);
          if (canRoute()) routeNow();
        });

        // Atualiza buffer ao arrastar a rota (tanto na criação quanto na edição)
        // Se já houver um polígono, recalcula ao arrastar
        window.google.maps.event.addListener(
          directionsRenderer.current,
          "directions_changed",
          () => {
            const res = directionsRenderer.current.getDirections();
            if (!res) return;
            latestDirections.current = res;

            // Atualiza os marcadores de origem e destino quando a rota é arrastada
            if (res.routes && res.routes[0] && res.routes[0].legs && res.routes[0].legs[0]) {
              const leg = res.routes[0].legs[0];

              // Atualiza marcador de origem
              if (leg.start_location && originMarker.current) {
                originMarker.current.setPosition(leg.start_location);
                // Atualiza o estado do input de origem
                reverseGeocodeToInput(leg.start_location, "origin");
              }

              // Atualiza marcador de destino
              if (leg.end_location && destinationMarker.current) {
                destinationMarker.current.setPosition(leg.end_location);
                // Atualiza o estado do input de destino
                reverseGeocodeToInput(leg.end_location, "destination");
              }
            }

            // Se já existe um polígono, recalcula ao arrastar a rota
            if (bufferPolygon.current) {
              buildBufferFromDirections(res);
            }
          }
        );

        // Força redimensionamento do mapa após um pequeno delay
        setTimeout(() => {
          if (map) {
            window.google.maps.event.trigger(map, 'resize');
          }
        }, 100);

        // 🔥 NOVO: Se estiver editando e ainda não carregou este item, carrega os dados
        // Verifica também se não está no meio de um carregamento e se não está usando o novo fluxo
        if (editingItem &&
          loadedEditItemIdRef.current !== editingItem.markerId &&
          !isLoadingEditDataRef.current &&
          !isUsingNewFlowRef.current) {
          setTimeout(() => {
            loadEditingData(editingItem);
          }, 300);
        }
      };

      // Inicializa o mapa (com retry se o elemento não estiver pronto)
      initMap();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, editingItem]); // Adiciona editingItem como dependência

  // 🔥 NOVA FUNÇÃO: Carregar dados do item em edição
  const loadEditingData = async (item) => {
    if (!mapInstance.current) return;

    // Evita carregar o mesmo item múltiplas vezes
    if (loadedEditItemIdRef.current === item.markerId) {
      return;
    }

    loadedEditItemIdRef.current = item.markerId;
    console.log("Carregando dados de edição:", item);

    // Limpa qualquer polígono, marcadores e rotas existentes
    if (bufferPolygon.current) {
      bufferPolygon.current.setMap(null);
      bufferPolygon.current = null;
    }

    if (latestPolyline.current) {
      latestPolyline.current.setMap(null);
      latestPolyline.current = null;
    }

    clearMarkers();
    clearLayers();
    directionsRenderer.current.set("directions", null);

    // 1. CRIA OS MARCADORES DE ORIGEM E DESTINO USANDO OS DADOS DA API
    const originPoint = {
      lat: item.latitude,
      lng: item.longitude
    };

    const destinationPoint = {
      lat: item.destinationLatitude || item.latitude,
      lng: item.destinationLongitude || item.longitude
    };

    // Cria marcador de origem
    originMarker.current = new window.google.maps.Marker({
      position: originPoint,
      map: mapInstance.current,
      label: { text: "O", color: "#111827", fontWeight: "700" },
      draggable: true
    });

    // Cria marcador de destino
    destinationMarker.current = new window.google.maps.Marker({
      position: destinationPoint,
      map: mapInstance.current,
      label: { text: "D", color: "#111827", fontWeight: "700" },
      draggable: true
    });

    // 2. ATUALIZA OS ESTADOS DOS INPUTS COM OS DADOS DA API
    setOrigin(item.address || t("ELETRONIC_FENCES.ORIGIN_FENCE_EDITED"));
    setDestination(
      item.destinationAddress || item.address || t("ELETRONIC_FENCES.DESTINATION_FENCE_EDITED")
    );

    // 3. ATUALIZA O BUFFER DISTANCE COM O VALOR DA CERCA
    if (item.radius) {
      setBufferDistance(item.radius);
    }

    // 4. PREENCHE A DESCRIÇÃO PARA O MODAL
    setMarkerDescription(item.markerDescription || "");

    // 5. PARSEAR E DESENHAR O POLÍGONO SALVO (campo area)
    if (item.area && item.area !== "NA") {
      let coordinates;
      try {
        // Remove asteriscos e converte para objeto
        const areaString = item.area.replace(/\*/g, '"');
        coordinates = JSON.parse(areaString);
        console.log("Coordenadas parseadas:", coordinates);
      } catch (e) {
        console.error("Erro ao ler coordenadas do item:", e);
        // Adicione aqui o tratamento de toast/notificação se necessário
        // setToastParams(JSON.stringify({
        //   show: true,
        //   title: "Erro",
        //   body: 'Erro ao carregar as coordenadas da área.',
        //   color: 'danger'
        // }));

        // Se falhar ao parsear, tenta calcular a rota como fallback
        setTimeout(() => {
          if (canRoute()) {
            routeNow();
          }
        }, 500);
        return;
      }

      // Verifica se há coordenadas válidas
      if (!coordinates || coordinates.length < 3) {
        console.error("Coordenadas insuficientes:", coordinates);
        // Adicione aqui o tratamento de toast/notificação se necessário
        // setToastParams(JSON.stringify({
        //   show: true,
        //   title: "Erro",
        //   body: 'Área com coordenadas insuficientes.',
        //   color: 'danger'
        // }));

        // Se não houver coordenadas suficientes, calcula a rota como fallback
        setTimeout(() => {
          if (canRoute()) {
            routeNow();
          }
        }, 500);
        return;
      }

      // Converte para formato do Google Maps
      const gCoords = coordinates.map(coord => ({
        lat: Number(coord.lat),
        lng: Number(coord.lng)
      }));

      console.log("Coordenadas Google Maps:", gCoords);

      // Usa drawOriginalPolygon para garantir cores e propriedades corretas
      drawOriginalPolygon(gCoords);

      // Adiciona listeners para atualizar o editingItem quando o polígono é editado
      // Aguarda um pequeno delay para garantir que o polígono foi criado
      setTimeout(() => {
        if (bufferPolygon.current) {
          const updatePolygonCoordinates = () => {
            const newPaths = bufferPolygon.current.getPaths();
            const newCoordinates = [];
            newPaths.forEach(path => {
              path.forEach(latLng => {
                newCoordinates.push({
                  lat: round6(latLng.lat()),
                  lng: round6(latLng.lng())
                });
              });
            });
            setEditingItem(prev => ({
              ...prev,
              area: JSON.stringify(newCoordinates).replace(/"/g, "*")
            }));
          };

          bufferPolygon.current.addListener("mouseup", updatePolygonCoordinates);
          bufferPolygon.current.addListener("dragend", updatePolygonCoordinates);
        }
      }, 50);

      // Centraliza o mapa no polígono
      const bounds = new window.google.maps.LatLngBounds();
      gCoords.forEach(coord => {
        bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
      });
      mapInstance.current.fitBounds(bounds);

      console.log("Polígono de edição carregado com sucesso");
    } else {
      // Se não houver polígono salvo, calcula a rota como fallback
      setTimeout(() => {
        if (canRoute()) {
          routeNow();
        }
      }, 500);
      return;
    }

    // 6. CRIA A ROTA TRACEJADA ENTRE OS MARCADORES (não usa Directions API)
    latestPolyline.current = new window.google.maps.Polyline({
      path: [originPoint, destinationPoint],
      geodesic: true,
      strokeColor: "#1e40af",
      strokeOpacity: 1.0,
      strokeWeight: 3,
      strokeDashArray: [10, 10], // Linha tracejada
      map: mapInstance.current
    });

    // 7. LISTENERS PARA ATUALIZAR A LINHA TRACEJADA QUANDO OS MARCADORES SE MOVEM
    const updatePolyline = () => {
      if (originMarker.current && destinationMarker.current && latestPolyline.current) {
        const originPos = originMarker.current.getPosition();
        const destPos = destinationMarker.current.getPosition();
        latestPolyline.current.setPath([originPos, destPos]);
      }
    };

    originMarker.current.addListener("dragend", updatePolyline);
    destinationMarker.current.addListener("dragend", updatePolyline);
  };

  // Inicializar autocomplete após os inputs serem renderizados
  useEffect(() => {

    if (activeTab !== "map") return; // Só inicializa quando estiver na aba do mapa

    const initializeAutocomplete = () => {

      if (!window.google || !window.google.maps || !window.google.maps.places) {
        setTimeout(initializeAutocomplete, 100);
        return;
      }

      if (originInputRef.current && destinationInputRef.current) {
        // Limpa autocompletes anteriores se existirem
        if (autocompleteOrigin.current) {
          window.google.maps.event.clearInstanceListeners(autocompleteOrigin.current);
          autocompleteOrigin.current = null;
        }
        if (autocompleteDestination.current) {
          window.google.maps.event.clearInstanceListeners(autocompleteDestination.current);
          autocompleteDestination.current = null;
        }

        // Inicializar autocomplete exatamente como no exemplo HTML
        if (originInputRef.current && destinationInputRef.current) {
          autocompleteOrigin.current =
            new window.google.maps.places.Autocomplete(originInputRef.current, {
              fields: ["geometry", "formatted_address"],
              componentRestrictions: { country: ["br"] }, // Restringe ao Brasil
            });
          autocompleteDestination.current =
            new window.google.maps.places.Autocomplete(
              destinationInputRef.current,
              {
                fields: ["geometry", "formatted_address"],
                componentRestrictions: { country: ["br"] }, // Restringe ao Brasil
              }
            );
        } else {
          return;
        }

        autocompleteOrigin.current.addListener("place_changed", () => {
          const place = autocompleteOrigin.current.getPlace();
          if (place?.geometry?.location) {
            const ll = place.geometry.location;
            const address = place.formatted_address || place.name || "";
            setOrigin(address); // Atualiza o estado do React
            setMarker("origin", ll, "O");
            if (canRoute()) routeNow();
          }
        });

        autocompleteDestination.current.addListener("place_changed", () => {
          const place = autocompleteDestination.current.getPlace();
          if (place?.geometry?.location) {
            const ll = place.geometry.location;
            const address = place.formatted_address || place.name || "";
            setDestination(address); // Atualiza o estado do React
            setMarker("destination", ll, "D");
            if (canRoute()) routeNow();
          }
        });
        console.log("Autocomplete inicializado com sucesso!");
      } else {
        // Se os inputs ainda não estão prontos, tenta novamente
        setTimeout(initializeAutocomplete, 100);
      }
    };

    // Aguarda um pouco para garantir que os inputs estão renderizados
    setTimeout(initializeAutocomplete, 200);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, editingItem, viewingItem]);

  // Função de inversão completa
  const swapOriginDestination = () => {
    // Troca posições dos marcadores
    const tempMarker = originMarker.current;
    originMarker.current = destinationMarker.current;
    destinationMarker.current = tempMarker;

    // Troca textos dos inputs
    const tempOrigin = origin;
    setOrigin(destination);
    setDestination(tempOrigin);

    // Atualiza labels dos marcadores
    if (originMarker.current)
      originMarker.current.setLabel({
        text: "O",
        color: "#111827",
        fontWeight: "700",
      });
    if (destinationMarker.current)
      destinationMarker.current.setLabel({
        text: "D",
        color: "#111827",
        fontWeight: "700",
      });

    if (canRoute()) routeNow();
  };

  const onSubmit = async () => {
    setSavingFence(true);
    // VALIDAÇÃO INICIAL
    if (!markerDescription.trim()) {
      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.ATTENTION"),
          body: t("ELETRONIC_FENCES.PLEASE_ENTER_FENCE_DESCRIPTION"),
          color: "warning",
        })
      );
      setShowToast(true);
      setSavingFence(false);
      return;
    }

    // CASO 1: EDIÇÃO DE CERCA EXISTENTE
    if (editingItem) {
      try {
        const coordinates = [];

        if (bufferPolygon.current) {
          // Extrai as coordenadas atualizadas do polígono editável
          const paths = bufferPolygon.current.getPaths();

          paths.forEach(path => {
            path.forEach(latLng => {
              coordinates.push({
                lat: round6(latLng.lat()),
                lng: round6(latLng.lng())
              });
            });
          });
        } else if (latestBufferGeoJSON.current?.geometry) {
          const geom = latestBufferGeoJSON.current.geometry;
          const ring = geom.type === "Polygon" ? geom.coordinates[0] : geom.coordinates[0][0];
          coordinates.push(
            ...roundLngLatPairs6(ring).map(([lng, lat]) => ({ lat, lng }))
          );
        }

        // Valida se há coordenadas suficientes
        if (coordinates.length < 3) {
          setToastParams(
            JSON.stringify({
              show: true,
              title: t("ELETRONIC_FENCES.INVALID_POLYGON_TITLE"),
              body: t("ELETRONIC_FENCES.INVALID_POLYGON_MIN_POINTS"),
              color: "warning",
            })
          );
          setShowToast(true);
          setSavingFence(false);
          return;
        }

        // Obtém as posições atuais dos marcadores (igual ao modo de criação)
        const originPos = originMarker.current?.getPosition();
        const destinationPos = destinationMarker.current?.getPosition();

        // Extrai o caminho da rota (areaPath)
        const areaPath = getRoutePath();

        // Prepara payload para atualização
        const payload = {
          markerTypeId: 3,
          markerId: editingItem.markerId,
          markerDescription: markerDescription.trim(),
          latitude: originPos ? originPos.lat() : editingItem.latitude,
          longitude: originPos ? originPos.lng() : editingItem.longitude,
          address: origin || editingItem.address,
          destinationLatitude: destinationPos ? destinationPos.lat() : (editingItem.destinationLatitude || editingItem.latitude),
          destinationLongitude: destinationPos ? destinationPos.lng() : (editingItem.destinationLongitude || editingItem.longitude),
          destinationAddress: destination || editingItem.destinationAddress || editingItem.address,
          radius: bufferDistance, // Usa o buffer distance atual
          area: JSON.stringify(coordinates),
          areaPath: areaPath, // Caminho da rota (JSON)
        };

        console.log("📤 Payload de atualização:", payload);

        // Chamada à API
        const response = await controlService.updateCoordinate(payload);

        if (response) {
          console.log("✅ Cerca eletrônica atualizada com sucesso:", response);

          // Mostra toast de sucesso
          setToastParams(
            JSON.stringify({
              show: true,
              title: t("COMMON.SUCCESS"),
              body: t("ELETRONIC_FENCES.SAVED_SUCCESS"),
              color: "success",
            })
          );
          setShowToast(true);

          // Atualiza a lista de dados
          await fetchData();

          // Limpa estados
          setEditingItem(null);
          setMarkerDescription("");
          setShowModal(false);

          // Navega para a aba de Cercas e remove o ID da rota
          setActiveTab("grid");
          if (params.id) {
            navigate("/electric-fence");
          }

        } else {
          throw new Error("Resposta não bem-sucedida da API");
        }

      } catch (error) {
        console.error("❌ Erro ao atualizar cerca:", error);
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("COMMON.ERROR"),
            body: t("ELETRONIC_FENCES.ERROR_UPDATE_FENCE"),
            color: "danger",
          })
        );
        setShowToast(true);
      }
      setSavingFence(false);
      return;
    }

    // CASO 2: CRIAÇÃO DE NOVA CERCA (CÓDIGO ORIGINAL MODIFICADO)
    if (!originMarker.current || !destinationMarker.current) {
      setToastParams(
        JSON.stringify({
          show: true,
          title: t("ELETRONIC_FENCES.INCOMPLETE_POINTS_TITLE"),
          body: t("ELETRONIC_FENCES.DEFINE_ORIGIN_DESTINATION_BEFORE_SAVE"),
          color: "warning",
        })
      );
      setShowToast(true);
      setSavingFence(false);
      return;
    }

    // Se não houver buffer calculado, calcula agora a partir da rota
    if (!latestBufferGeoJSON.current && latestDirections.current) {
      const centerLatLngs = buildCenterlineLatLngsFromSteps(latestDirections.current);
      if (centerLatLngs.length > 0) {
        const coords = centerLatLngs
          .map((ll) => [ll.lng(), ll.lat()])
          .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat));

        if (coords.length >= 2) {
          let line = turf.lineString(coords);
          line = turf.cleanCoords(line);
          const radiusKm = Math.max(0.01, (bufferDistance || 500) / 2000);
          const buffered = turf.buffer(line, radiusKm, {
            units: "kilometers",
            steps: 128,
          });
          latestBufferGeoJSON.current = buffered;
        }
      }
    }

    if (!latestBufferGeoJSON.current) {
      setToastParams(
        JSON.stringify({
          show: true,
          title: t("ELETRONIC_FENCES.ROUTE_NOT_CALCULATED_TITLE"),
          body: t("ELETRONIC_FENCES.CALCULATE_ROUTE_BEFORE_SAVE"),
          color: "warning",
        })
      );
      setShowToast(true);
      setSavingFence(false);
      return;
    }

    try {
      const originPos = originMarker.current.getPosition();
      const destinationPos = destinationMarker.current.getPosition();

      // Extrai os vértices do buffer atual
      const geom = latestBufferGeoJSON.current.geometry;
      const ring = geom.type === "Polygon" ? geom.coordinates[0] : geom.coordinates[0][0];
      const verts = roundLngLatPairs6(ring).map(([lng, lat]) => ({ lat, lng }));

      // Converte para string (formato compatível)
      const polygonString = JSON.stringify(verts).replace(/"/g, "*");

      // Extrai o caminho da rota (areaPath)
      const areaPath = getRoutePath();

      // Prepara payload para criação
      const payload = {
        markerTypeId: 3,
        markerDescription: markerDescription.trim(),
        latitude: originPos.lat(),
        longitude: originPos.lng(),
        address: origin,
        destinationLatitude: destinationPos.lat(),
        destinationLongitude: destinationPos.lng(),
        destinationAddress: destination,
        radius: bufferDistance,
        area: polygonString,
        areaPath: areaPath, // Caminho da rota (JSON)
      };

      console.log("📤 Payload de criação:", payload);

      // Chamada à API
      const response = await controlService.createFences(payload);

      if (response?.status >= 200 && response?.status < 300) {
        console.log("✅ Cerca eletrônica criada com sucesso:", response);

        // Mostra toast de sucesso
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("COMMON.SUCCESS"),
            body: t("ELETRONIC_FENCES.SAVED_SUCCESS"),
            color: "success",
          })
        );
        setShowToast(true);

        // Atualiza a lista de dados
        await fetchData();

        // Limpa estados e mapa
        setMarkerDescription("");
        setShowModal(false);
        clearAll();

        // Navega para a aba de Cercas e remove o ID da rota
        setActiveTab("grid");
        if (params.id) {
          navigate("/electric-fence");
        }

      } else if (response?.success !== true) {
        // Caso a API retorne success false mas status 200 - trata como sucesso
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("COMMON.SUCCESS"),
            body: t("ELETRONIC_FENCES.SAVED_SUCCESS"),
            color: "success",
          })
        );
        setShowToast(true);

        // Atualiza a lista de dados
        await fetchData();

        // Limpa estados e mapa
        setMarkerDescription("");
        setShowModal(false);
        clearAll();

        // Navega para a aba de Cercas e remove o ID da rota
        setActiveTab("grid");
        if (params.id) {
          navigate("/electric-fence");
        }
      } else {
        throw new Error("Resposta não bem-sucedida da API");
      }

    } catch (error) {
      console.error("❌ Erro ao criar cerca:", error);
      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.ERROR"),
          body: t("ELETRONIC_FENCES.ERROR_SAVE_FENCE"),
          color: "danger",
        })
      );
      setShowToast(true);
    } finally {
      setSavingFence(false);
    }
  };

  const onSubmitModalEdit = async () => {
    setSavingFence(true);
    if (!selectedBindMarker) {
      console.error("Nenhum marcador selecionado para edição.");
      setSavingFence(false);
      return;
    }

    // Extrai o caminho da rota (areaPath) se disponível
    const areaPath = getRoutePath();

    const payload = {
      markerTypeId: 3,
      markerId: selectedBindMarker.markerId, // vem do handleEdit
      markerDescription:
        selectedBindMarker.markerDescription || t("ELETRONIC_FENCES.GENERATED_BY_MAP"),
      latitude: selectedBindMarker.latitude,
      longitude: selectedBindMarker.longitude,
      address: selectedBindMarker.address,
      destinationLatitude: selectedBindMarker.latitude, // igual ao original
      destinationLongitude: selectedBindMarker.longitude,
      destinationAddress: selectedBindMarker.address,
      radius: selectedBindMarker.radius,
      area: selectedBindMarker.area, // mantém compatibilidade com formato de cerca
      areaPath: areaPath, // Caminho da rota (JSON)
    };

    try {
      const response = await controlService.updateCoordinate(payload);
      if (response) {
        console.log("✅ Cerca eletrônica atualizada com sucesso:", response);
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("ELETRONIC_FENCES.FENCE_UPDATED_TITLE"),
            body: t("ELETRONIC_FENCES.FENCE_UPDATED_SUCCESS"),
            color: "success",
          })
        );
        try {
          const list = await controlService.findCoordinate(3);
          if (list) setData(list);
        } catch (e) {
          console.error(e);
        }
      } else {
        console.error("❌ Erro na resposta da API:", response);
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("COMMON.ERROR"),
            body: t("ELETRONIC_FENCES.ERROR_UPDATE_FENCE_SHORT"),
            color: "danger",
          })
        );
      }
    } catch (error) {
      console.error("Erro ao atualizar coordenada:", error);
      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.ERROR"),
          body: t("ELETRONIC_FENCES.ERROR_UPDATE_FENCE_SHORT"),
          color: "danger",
        })
      );
    } finally {
      setShowToast(true);
      setShowModalEdit(false);
      setSavingFence(false);
    }
  };

  const clearEditingState = () => {
    loadedEditItemIdRef.current = null; // Reseta a flag de item carregado
    isUsingNewFlowRef.current = false; // Reseta a flag do novo fluxo
    setEditingItem(null);
    setMarkerDescription("");
    setIsViewMode(false);
    setViewingItem(null);

    // Limpa polígono
    if (bufferPolygon.current) {
      bufferPolygon.current.setMap(null);
      bufferPolygon.current = null;
    }

    // Limpa polyline
    if (latestPolyline.current) {
      latestPolyline.current.setMap(null);
      latestPolyline.current = null;
    }

    // Remove o ID da rota se estiver em modo edição
    if (params.id) {
      navigate("/electric-fence");
    }
  };

  // Limpar tudo
  const clearAll = () => {
    loadedEditItemIdRef.current = null; // Reseta a flag de item carregado
    isUsingNewFlowRef.current = false; // Reseta a flag do novo fluxo
    clearLayers();
    clearMarkers();
    clearEditingState();
    directionsRenderer.current.set("directions", null);

    // Limpa a polyline tracejada
    if (latestPolyline.current) {
      latestPolyline.current.setMap(null);
      latestPolyline.current = null;
    }

    setOrigin("");
    setDestination("");
    setBufferDistance(500);
    setIsViewMode(false);
    setViewingItem(null);
  };

  // Aplicar buffer - cria o polígono ao redor da rota com a largura especificada
  const applyBuffer = () => {
    if (latestDirections.current) {
      buildBufferFromDirections(latestDirections.current);
    }
  };

  // Função para checkbox

  const onBindCheckboxChange = (checked, vehicleId) => {
    if (checked) {
      // Adiciona ao array se não estiver já presente
      setSelectedVehicleIds((prev) =>
        prev.includes(vehicleId) ? prev : [...prev, vehicleId]
      );
    } else {
      // Remove do array
      setSelectedVehicleIds((prev) => prev.filter((id) => id !== vehicleId));
    }
  };

  // 2. O Template do Checkbox que será usado no Grid do Modal
  const bindCheckboxTemplate = (props) => {
    const isChecked = selectedVehicleIds.includes(props.vehicleId);
    return (
      <input
        type="checkbox"
        checked={isChecked}
        onChange={(e) =>
          onBindCheckboxChange(e.target.checked, props.vehicleId)
        }
      />
    );
  };

  // Função para salvar as vinculações
  const handleSaveBindings = async () => {
    if (!selectedBindMarker) {
      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.ATTENTION"),
          body: t("GROUP_USERS.NO_MARKER_SELECTED"),
          color: "Warning",
        })
      );
      return;
    }

    setLoading(true);
    try {
      const markerTypeId = selectedBindMarker.markerTypeId;

      // PATCH: todos os veículos que estão marcados (checados)
      const vehiclesToBind = selectedVehicleIds;

      // DELETE: todos os veículos que estão na lista mas não estão marcados (não checados)
      const allVehicleIds = bindList.map((item) => item.vehicleId);
      const vehiclesToUnbind = allVehicleIds.filter(
        (id) => !selectedVehicleIds.includes(id)
      );

      const promises = [];

      // Sempre enviar PATCH com os marcados
      const bindPayload = {
        listVehicleId: vehiclesToBind,
        markerId: selectedBindMarker.markerId,
        markerTypeId: markerTypeId,
      };
      promises.push(controlService.bindPointControl(bindPayload));

      // Sempre enviar DELETE com os não marcados
      if (vehiclesToUnbind.length > 0) {
        const unbindPayload = {
          listVehicleId: vehiclesToUnbind,
          markerId: selectedBindMarker.markerId,
          markerTypeId: markerTypeId,
        };
        promises.push(controlService.unbindPointControl(unbindPayload));
      }

      const responses = await Promise.all(promises);
      const hasError = responses.some((res) => !res || res.error);

      if (!hasError) {
        const totalChanges = vehiclesToBind.length + vehiclesToUnbind.length;
        await controlService.updateMarkers();
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("COMMON.SUCCESS"),
            body: `${totalChanges} ${t("GROUP_USERS.CHANGES_SAVED")}`,
            color: "Success",
          })
        );

        setSelectedVehicleIds([]);
        setShowModalControl(false);
      } else {
        const body =
          responses.find((r) => r?.message)?.message ||
          responses.find((r) => r?.friendlyMessage)?.friendlyMessage ||
          t("GROUP_USERS.SAVE_ERROR");
        setToastParams(
          JSON.stringify({ show: true, title: t("COMMON.ERROR"), body, color: "Danger" })
        );
      }
    } catch (error) {
      console.error("Erro ao salvar vinculações:", error);
      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.ERROR"),
          body: t("GROUP_USERS.REQUEST_ERROR"),
          color: "Danger",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  // Função para calcular o pageSize baseado na altura da tela
  const calculatePageSize = () => {
    const windowHeight = window.innerHeight;

    // Altura dos elementos fixos baseado no layout atual
    let headerHeight = 60; // Header da aplicação (reduzido)
    let cardPadding = 30; // Padding do card (20px top + 20px bottom)
    let titleHeight = 50; // Título e controles (aumentado para incluir radio buttons)
    let gridHeaderHeight = 35; // Cabeçalho do grid
    let paginationHeight = 30; // Paginação (aumentado)
    let rowHeight = 38; // Altura de uma linha

    // Altura total dos elementos fixos
    const totalFixedHeight =
      headerHeight +
      cardPadding +
      titleHeight +
      gridHeaderHeight +
      paginationHeight;

    // Altura disponível para as linhas
    const availableHeight = windowHeight - totalFixedHeight;

    // Calcular quantas linhas cabem
    const calculatedPageSize = Math.max(
      Math.floor(availableHeight / rowHeight),
      10
    );

    // Removido console.log para evitar spam no console
    // console.log("Calculando pageSize:", {
    //   windowHeight,
    //   totalFixedHeight,
    //   availableHeight,
    //   rowHeight,
    //   calculatedPageSize,
    // });

    setPageSize(calculatedPageSize);
  };

  // useLayoutEffect para calcular pageSize quando o componente é montado
  useLayoutEffect(() => {
    calculatePageSize();
  }, [location.pathname]);

  // useLayoutEffect para calcular pageSize quando a opção muda
  // useLayoutEffect(() => {
  //     calculatePageSize();
  // }, [opcaoSelecionada]);

  // useLayoutEffect para calcular pageSize quando o show muda
  // useLayoutEffect(() => {
  //     if (show) {
  //         calculatePageSize();
  //     }
  // }, [show]);

  // useLayoutEffect para calcular pageSize quando os dados são carregados
  // useLayoutEffect(() => {
  //     if (dataFilterVisits?.length > 0 || dataFilter?.vehiclesUser?.length > 0 || dataFilterAddress?.length > 0) {
  //         calculatePageSize();
  //     }
  // }, [dataFilterVisits, dataFilter, dataFilterAddress]);

  // useLayoutEffect para calcular pageSize quando o loading termina
  // useLayoutEffect(() => {
  //     if (!dataLoading) {
  //         calculatePageSize();
  //     }
  // }, [dataLoading]);

  // Effect para redimensionamento da janela com debounce
  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      // Debounce: aguarda 300ms após o último resize antes de recalcular
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        calculatePageSize();
      }, 300);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  const fetchGroups = async () => {
    try {
      const groups = await vehicleGroupService.fetchGroups();
      setVehicleGroups(groups || []);
    } catch (error) {
      console.error("Erro ao buscar grupos:", error);
    }
  };

  const fetchBindsData = async () => {
    try {
      const vehicleGroupId =
        selectedGroup && selectedGroup !== "0" && selectedGroup !== "TODOS"
          ? selectedGroup
          : null;
      const response = await controlService.fetchBindPointControl(
        selectedBindMarker.markerTypeId,
        selectedBindMarker.markerId,
        vehicleGroupId
      );

      if (response) {
        setBindList(response);
        // Inicializa o array de selecionados com os veículos que têm markerId > 0
        const alreadyLinked = response
          .filter((item) => item.markerId && item.markerId > 0)
          .map((item) => item.vehicleId);
        setSelectedVehicleIds(alreadyLinked);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchData = async () => {
    setDataLoading(true);
    try {
      const response = await controlService.findCoordinate(3);

      if (response) {
        setData(response);
      }
    } catch (error) {
      console.error(error);
      setToastParams(
        JSON.stringify({
          show: true,
          title: t("COMMON.ERROR"),
          body: t("ELETRONIC_FENCES.ERROR_LOADING_FENCES"),
          color: "danger",
        })
      );
      setShowToast(true);
    } finally {
      setDataLoading(false);
    }
  };

  // Fetch vehicle groups when modal opens
  useEffect(() => {
    if (showModalControl) {
      fetchGroups();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModalControl]);

  useEffect(() => {
    if (!selectedBindMarker) return; // só faz após clicar no botão

    fetchBindsData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBindMarker, showModalControl, selectedGroup]);

  useEffect(() => {
    if (activeTab === "grid") {
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Carrega dados quando há ID na rota (modo edição)
  useEffect(() => {
    const editId = params.id;

    // Se não há ID, reseta as flags
    if (!editId) {
      isLoadingEditDataRef.current = false;
      loadedEditItemIdRef.current = null;
      return;
    }

    // Evita múltiplas execuções
    if (isLoadingEditDataRef.current) {
      return;
    }

    // Se já está editando este item, não precisa carregar novamente
    if (editingItem && editingItem.markerId === parseInt(editId)) {
      return;
    }

    // Reseta a flag de item carregado quando muda o ID
    if (loadedEditItemIdRef.current !== parseInt(editId)) {
      loadedEditItemIdRef.current = null;
    }

    isLoadingEditDataRef.current = true;

    // Busca o item específico pelo ID
    // Primeiro tenta encontrar nos dados já carregados (mais rápido)
    const itemFromCache = data && Array.isArray(data) ? data.find(item => item.markerId === parseInt(editId)) : null;

    if (itemFromCache) {
      // Usa dados do cache (muito mais rápido)
      const editable = {
        ...itemFromCache,
        markerDescription: itemFromCache.markerDescription || "",
        latitude: Number(itemFromCache.latitude),
        longitude: Number(itemFromCache.longitude),
        address: itemFromCache.address || itemFromCache.location || "",
        area: itemFromCache.area || "NA",
        radius: itemFromCache.radius || 0,
        markerTypeId: 3,
        markerId: itemFromCache.markerId,
        destinationLatitude: itemFromCache.destinationLatitude || itemFromCache.latitude,
        destinationLongitude: itemFromCache.destinationLongitude || itemFromCache.longitude,
        destinationAddress: itemFromCache.destinationAddress || itemFromCache.address || itemFromCache.location || "",
      };

      // Primeiro muda para a aba do mapa para garantir que o elemento esteja renderizado
      setActiveTab("map");

      // Depois define o item para edição (com pequeno delay para garantir que o mapa seja criado)
      setTimeout(() => {
        setEditingItem(editable);
        setMarkerDescription(editable.markerDescription);
        isLoadingEditDataRef.current = false;
      }, 100);

      return; // Sai aqui se encontrou no cache
    }

    // Se não encontrou no cache, busca na API
    const loadEditData = async () => {
      try {
        const response = await controlService.findCoordinate(3);

        if (response && Array.isArray(response)) {
          const itemToEdit = response.find(item => item.markerId === parseInt(editId));

          if (itemToEdit) {
            // Prepara o item para edição
            const editable = {
              ...itemToEdit,
              markerDescription: itemToEdit.markerDescription || "",
              latitude: Number(itemToEdit.latitude),
              longitude: Number(itemToEdit.longitude),
              address: itemToEdit.address || itemToEdit.location || "",
              area: itemToEdit.area || "NA",
              radius: itemToEdit.radius || 0,
              markerTypeId: 3,
              markerId: itemToEdit.markerId,
              destinationLatitude: itemToEdit.destinationLatitude || itemToEdit.latitude,
              destinationLongitude: itemToEdit.destinationLongitude || itemToEdit.longitude,
              destinationAddress: itemToEdit.destinationAddress || itemToEdit.address || itemToEdit.location || "",
            };

            // Primeiro muda para a aba do mapa para garantir que o elemento esteja renderizado
            setActiveTab("map");

            // Depois define o item para edição (com pequeno delay para garantir que o mapa seja criado)
            setTimeout(() => {
              setEditingItem(editable);
              setMarkerDescription(editable.markerDescription);
              isLoadingEditDataRef.current = false;
            }, 100);
          } else {
            // Item não encontrado
            isLoadingEditDataRef.current = false;
            setToastParams(
              JSON.stringify({
                show: true,
                title: t("COMMON.ERROR"),
                body: t("ELETRONIC_FENCES.FENCE_NOT_FOUND"),
                color: "danger",
              })
            );
            setShowToast(true);
            // Volta para a lista
            navigate("/electric-fence");
          }
        }
      } catch (error) {
        isLoadingEditDataRef.current = false;
        console.error("Erro ao carregar dados para edição:", error);
        setToastParams(
          JSON.stringify({
            show: true,
            title: t("COMMON.ERROR"),
            body: t("ELETRONIC_FENCES.ERROR_LOADING_EDIT_DATA"),
            color: "danger",
          })
        );
        setShowToast(true);
        navigate("/electric-fence");
      }
    };

    loadEditData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id, navigate]); // Removido 'data' das dependências para evitar loops



  // useEffect corrigido:
  // useEffect(() => {
  //   // Só roda se estivermos na aba "map", com o mapa pronto E um item em edição
  //   if (activeTab === "map" && editingItem && mapInstance.current) {
  //     console.log("Editando item:", editingItem);

  //     // Limpa qualquer polígono, marcadores e rotas existentes
  //     if (bufferPolygon.current) {
  //       bufferPolygon.current.setMap(null);
  //       bufferPolygon.current = null;
  //     }

  //     // Limpa a polyline anterior
  //     if (latestPolyline.current) {
  //       latestPolyline.current.setMap(null);
  //       latestPolyline.current = null;
  //     }

  //     clearMarkers();
  //     clearLayers();
  //     directionsRenderer.current.set("directions", null);

  //     // Converte as coordenadas (que vêm como string do DB) para objeto
  //     let coordinates;
  //     try {
  //       // Remove asteriscos e converte para objeto
  //       const areaString = editingItem.area.replace(/\*/g, '"');
  //       coordinates = JSON.parse(areaString);
  //     } catch (e) {
  //       console.error("Erro ao ler coordenadas do item:", e);
  //       return;
  //     }

  //     // 1. CRIA O POLÍGONO EDITÁVEL
  //     const polygon = new window.google.maps.Polygon({
  //       paths: coordinates,
  //       map: mapInstance.current,
  //       editable: true,
  //       draggable: true,
  //       strokeColor: "#FF0000",
  //       strokeOpacity: 0.8,
  //       strokeWeight: 2,
  //       fillColor: "#FF0000",
  //       fillOpacity: 0.35,
  //     });

  //     // Guarda a referência do polígono ativo
  //     bufferPolygon.current = polygon;

  //     // 2. CRIA OS MARCADORES DE ORIGEM E DESTINO USANDO OS DADOS DA API
  //     const originPoint = {
  //       lat: editingItem.latitude,
  //       lng: editingItem.longitude
  //     };

  //     const destinationPoint = {
  //       lat: editingItem.destinationLatitude || editingItem.latitude,
  //       lng: editingItem.destinationLongitude || editingItem.longitude
  //     };

  //     // Cria marcador de origem
  //     originMarker.current = new window.google.maps.Marker({
  //       position: originPoint,
  //       map: mapInstance.current,
  //       label: { text: "O", color: "#111827", fontWeight: "700" },
  //       draggable: true
  //     });

  //     // Cria marcador de destino
  //     destinationMarker.current = new window.google.maps.Marker({
  //       position: destinationPoint,
  //       map: mapInstance.current,
  //       label: { text: "D", color: "#111827", fontWeight: "700" },
  //       draggable: true
  //     });

  //     // 3. ATUALIZA OS ESTADOS DOS INPUTS COM OS DADOS DA API
  //     setOrigin(editingItem.address || "Origem da cerca editada");
  //     setDestination(editingItem.destinationAddress || editingItem.address || "Destino da cerca editada");

  //     // 4. CRIA A ROTA TRACEJADA ENTRE OS MARCADORES
  //     const polyline = new window.google.maps.Polyline({
  //       path: [originPoint, destinationPoint],
  //       geodesic: true,
  //       strokeColor: "#1e40af",
  //       strokeOpacity: 1.0,
  //       strokeWeight: 3,
  //       strokeDashArray: [10, 10], // Linha tracejada
  //       map: mapInstance.current
  //     });

  //     // 5. CENTRALIZA O MAPA NO POLÍGONO
  //     const bounds = new window.google.maps.LatLngBounds();
  //     coordinates.forEach(coord => {
  //       bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
  //     });
  //     mapInstance.current.fitBounds(bounds);

  //     // 6. ATUALIZA O BUFFER DISTANCE COM O VALOR DA CERCA
  //     if (editingItem.radius) {
  //       setBufferDistance(editingItem.radius);
  //     }

  //     // 7. LISTENERS PARA ATUALIZAÇÕES EM TEMPO REAL

  //     // Listener para edição do polígono
  //     polygon.addListener("mouseup", () => {
  //       const newPaths = polygon.getPaths();
  //       const newCoordinates = [];

  //       newPaths.forEach(path => {
  //         path.forEach(latLng => {
  //           newCoordinates.push({
  //             lat: round6(latLng.lat()),
  //             lng: round6(latLng.lng())
  //           });
  //         });
  //       });

  //       // Atualiza o editingItem com as novas coordenadas
  //       setEditingItem(prev => ({
  //         ...prev,
  //         area: JSON.stringify(newCoordinates).replace(/"/g, "*")
  //       }));
  //     });

  //     // Listener para arrastar o polígono inteiro
  //     polygon.addListener("dragend", () => {
  //       const newPaths = polygon.getPaths();
  //       const newCoordinates = [];

  //       newPaths.forEach(path => {
  //         path.forEach(latLng => {
  //           newCoordinates.push({
  //             lat: round6(latLng.lat()),
  //             lng: round6(latLng.lng())
  //           });
  //         });
  //       });

  //       setEditingItem(prev => ({
  //         ...prev,
  //         area: JSON.stringify(newCoordinates).replace(/"/g, "*")
  //       }));
  //     });

  //     // Função para atualizar a linha tracejada quando os marcadores se movem
  //     const updatePolyline = () => {
  //       if (originMarker.current && destinationMarker.current) {
  //         const originPos = originMarker.current.getPosition();
  //         const destPos = destinationMarker.current.getPosition();
  //         polyline.setPath([originPos, destPos]);
  //       }
  //     };

  //     // Listeners para arrastar os marcadores
  //     originMarker.current.addListener("dragend", updatePolyline);
  //     destinationMarker.current.addListener("dragend", updatePolyline);

  //     // 8. GUARDA REFERÊNCIAS PARA LIMPEZA
  //     latestPolyline.current = polyline;

  //     // 9. PREENCHE A DESCRIÇÃO PARA O MODAL
  //     setMarkerDescription(editingItem.markerDescription || "");
  //   }

  //   // Cleanup function
  //   return () => {
  //     if (latestPolyline.current) {
  //       latestPolyline.current.setMap(null);
  //       latestPolyline.current = null;
  //     }
  //   };
  // }, [activeTab, editingItem]);

  const handleViewOnMap = async (row) => {
    // Marca que estamos usando o novo fluxo
    isUsingNewFlowRef.current = true;

    try {
      // 1. CHAMA O ENDPOINT PARA BUSCAR OS DADOS DO POLÍGONO
      const polygonData = await controlService.findPolygon(row.markerId);

      console.log("Dados do polígono recebidos (visualização):", polygonData);

      // Prepara o item para visualização
      const viewable = {
        ...row,
        markerDescription: row.markerDescription || "",
        latitude: Number(row.latitude),
        longitude: Number(row.longitude),
        address: row.address || row.location || "",
        area: polygonData?.area || row.area || "NA",
        radius: row.radius || 0,
        markerTypeId: 3,
        markerId: row.markerId,
        destinationLatitude: row.destinationLatitude || row.latitude,
        destinationLongitude: row.destinationLongitude || row.longitude,
        destinationAddress: row.destinationAddress || row.address || row.location || "",
        areaPath: polygonData?.areaPath || null,
      };

      // Limpa qualquer estado de edição anterior
      setEditingItem(null);
      setViewingItem(viewable);
      setIsViewMode(true);

      // Muda para a aba do mapa
      setActiveTab("map");

      // Aguarda um pouco para garantir que o mapa está carregado
      setTimeout(async () => {
        if (!mapInstance.current) return;

        const map = mapInstance.current;

        // Limpa camadas existentes
        clearLayers();
        clearMarkers();
        directionsRenderer.current.set("directions", null);

        // Limpa a polyline anterior se existir
        if (latestPolyline.current) {
          latestPolyline.current.setMap(null);
          latestPolyline.current = null;
        }

        // 1. ATUALIZA OS ESTADOS DOS INPUTS COM OS DADOS
        setOrigin(row.address || "Origem da cerca");
        setDestination(row.destinationAddress || row.address || "Destino da cerca");

        // 2. ATUALIZA O BUFFER DISTANCE
        const currentBufferDistance = row.radius || 500;
        setBufferDistance(currentBufferDistance);

        // 3. CRIA OS MARCADORES DE ORIGEM E DESTINO (não arrastáveis em modo visualização)
        const originPoint = {
          lat: row.latitude,
          lng: row.longitude
        };

        const destinationPoint = {
          lat: row.destinationLatitude || row.latitude,
          lng: row.destinationLongitude || row.longitude
        };

        // Cria marcador de origem (não arrastável em modo visualização)
        originMarker.current = new window.google.maps.Marker({
          position: originPoint,
          map: map,
          label: { text: "O", color: "#111827", fontWeight: "700" },
          draggable: false
        });

        // Cria marcador de destino (não arrastável em modo visualização)
        destinationMarker.current = new window.google.maps.Marker({
          position: destinationPoint,
          map: map,
          label: { text: "D", color: "#111827", fontWeight: "700" },
          draggable: false
        });

        // 4. PLOTA A ROTA A PARTIR DO areaPath SE DISPONÍVEL
        const hasAreaPath = polygonData?.areaPath && polygonData.areaPath !== "" && polygonData.areaPath !== "NA";
        if (hasAreaPath) {
          // Aguarda um pequeno delay para garantir que bufferDistance foi atualizado
          setTimeout(() => {
            plotRouteFromAreaPath(polygonData.areaPath, currentBufferDistance);
          }, 150);
        } else {
          // 5. FALLBACK: Se não temos areaPath, calcula a rota automaticamente
          if (canRoute()) {
            directionsRenderer.current.setMap(map);
            const originPos = originMarker.current.getPosition();
            const destPos = destinationMarker.current.getPosition();

            try {
              const res = await directionsService.current.route({
                origin: originPos,
                destination: destPos,
                travelMode: window.google.maps.TravelMode.DRIVING,
              });

              latestDirections.current = res;
              directionsRenderer.current.setDirections(res);

              // Aplica o buffer automaticamente
              buildBufferFromDirections(res, currentBufferDistance);
            } catch (err) {
              setToastParams(
                JSON.stringify({
                  show: true,
                  title: t("COMMON.ERROR"),
                  body: t("ELETRONIC_FENCES.ERROR_ROUTE_DRAW"),
                  color: "danger",
                })
              );
              setShowToast(true);
            }
          }
        }
      }, 500);
    } catch (error) {
      console.error("Erro ao buscar dados do polígono (visualização):", error);
      // Em caso de erro, tenta carregar sem os dados do endpoint
      setViewingItem({
        ...row,
        markerDescription: row.markerDescription || "",
        latitude: Number(row.latitude),
        longitude: Number(row.longitude),
        address: row.address || row.location || "",
        area: row.area || "NA",
        radius: row.radius || 0,
        markerTypeId: 3,
        markerId: row.markerId,
        destinationLatitude: row.destinationLatitude || row.latitude,
        destinationLongitude: row.destinationLongitude || row.longitude,
        destinationAddress: row.destinationAddress || row.address || row.location || "",
      });
      setIsViewMode(true);
      setActiveTab("map");
    }
  };

  return (
    <>
      {/* Modal Edit */}
      <Modal
        show={showModalEdit}
        onHide={() => setShowModalEdit(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("VISIT_POINTS.EDIT_COORDINATE")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBindMarker && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>{t("COMMON.NAME")}:</Form.Label>
                <Form.Control
                  type="text"
                  value={selectedBindMarker.markerDescription}
                  onChange={(e) =>
                    setSelectedBindMarker({
                      ...selectedBindMarker,
                      markerDescription: e.target.value,
                    })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t("ELETRONIC_FENCES.RADIUS")}:</Form.Label>
                <Form.Select
                  className="form-control"
                  value={Number(selectedBindMarker.radius) || 50}
                  onChange={(e) =>
                    setSelectedBindMarker({
                      ...selectedBindMarker,
                      radius: Number(e.target.value),
                    })
                  }
                >
                  <option value="0">{t("VISIT_POINTS.RADIUS_SELECT")}</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="200">200</option>
                  <option value="500">500</option>
                  <option value="1000">1000</option>
                </Form.Select>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModalEdit(false)} disabled={savingFence}>
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button variant="primary" onClick={() => onSubmitModalEdit()} disabled={savingFence}>
            {savingFence ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {t("ELETRONIC_FENCES.SAVING")}
              </>
            ) : (
              t("BUTTONS.SAVE")
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Confirm Delete */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("ELETRONIC_FENCES.CONFIRM_DELETE")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {fenceToDelete && (
            <p>
              {t("ELETRONIC_FENCES.DELETE_MESSAGE")}{" "}
              <strong>{fenceToDelete.markerDescription}</strong>?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            {t("BUTTONS.DELETE")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Controle de Visitas */}
      <Modal
        show={showModalControl}
        onHide={() => setShowModalControl(false)}
        size="lg"
        scrollable
      >
        <Modal.Header
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "start",
            textAlign: "left",
          }}
        >
          <Modal.Title>{t("ELETRONIC_FENCES.BIND_VEHICLES")}</Modal.Title>
          <p>
            {t("ELETRONIC_FENCES.DELETE_WITHOUT_LINKED_VEHICLE_MSG")}
          </p>

          {/* Filtro de Grupos e Botões lado a lado */}
          <div
            style={{
              display: "flex",
              gap: 16,
              alignItems: "flex-end",
              marginTop: 15,
              marginBottom: 10,
            }}
          >
            {/* Filtro de Grupos */}
            <div style={{ flex: 1 }}>
              <Filters
                id={"gruposModal"}
                isMulti={false}
                title={t("GROUP_USERS.GROUPS")}
                pageTitle={"EletronicFences"}
                hasSubTitle={true}
                subTitle={t("LINKED_VEHICLES.ALL_GROUPS")}
                options={vehicleGroups || []}
                icon={HiOutlineRectangleGroup}
                hasIcon={true}
              />
            </div>

            {/* 🔹 Botões Selecionar/Desmarcar Todos */}
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                variant="primary"
                onClick={() => {
                  // Marca todos os veículos
                  const allIds = bindList.map((item) => item.vehicleId);
                  setSelectedVehicleIds(allIds);
                }}
              >
                {t("GROUP_USERS.SELECT_ALL")}
              </Button>

              <Button
                variant="secondary"
                onClick={() => {
                  // Desmarca todos os veículos
                  setSelectedVehicleIds([]);
                }}
              >
                {t("GROUP_USERS.REMOVE_ALL")}
              </Button>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {loading && <p>{t("GROUP_USERS.PROCESSING")}</p>}
          {bindList.length === 0 ? (
            <p
              style={{
                textAlign: "center",
                fontWeight: "500",
                padding: "20px 0",
              }}
            >
              {t("ELETRONIC_FENCES.NO_VEHICLES_FOUND_FOR_POINT")}
            </p>
          ) : (
            <GridComponent
              ref={gridRef}
              dataSource={bindList}
              allowPaging={false}
              cssClass="custom-grid-control"
              selectionSettings={{ type: "Multiple" }}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="licensePlate"
                  headerText={t("COMMON.PLATE")}
                  width="120"
                  textAlign="Center"
                />
                <ColumnDirective
                  headerText={t("GROUP_USERS.BIND")}
                  width="60"
                  textAlign="Center"
                  template={bindCheckboxTemplate}
                />
                <ColumnDirective
                  field="markerStatusDescription"
                  headerText={t("REPORT_COLUMNS.STATUS")}
                  width="100"
                  textAlign="Center"
                />
              </ColumnsDirective>

              <Inject services={[Edit]} />
            </GridComponent>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handleSaveBindings}
            disabled={loading}
          >
            {t("ELETRONIC_FENCES.SAVE_BINDINGS")}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setSelectedVehicleIds([]);
              setShowModalControl(false);
            }}
          >
            {t("BUTTONS.CLOSE")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Salvar Polígono */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingItem ? t("ELETRONIC_FENCES.EDIT_FENCE") : t("ELETRONIC_FENCES.CREATE_FENCE")}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3" controlId="markerDescription">
            <Form.Label>{t("RISK_AREA.AREA_DESCRIPTION")}:</Form.Label>
            <Form.Control
              type="text"
              placeholder={t("ELETRONIC_FENCES.AREA_DESCRIPTION_PLACEHOLDER")}
              value={markerDescription}
              onChange={(e) => setMarkerDescription(e.target.value)}
              maxLength={100}
            />
            {editingItem && (
              <Form.Text className="text-muted">
                {t("ELETRONIC_FENCES.EDITING")}: {editingItem.markerDescription}
              </Form.Text>
            )}
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowModal(false);
            setEditingItem(null);
            setMarkerDescription("");
          }} disabled={savingFence}>
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button variant="primary" onClick={onSubmit} disabled={savingFence}>
            {savingFence ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {t("ELETRONIC_FENCES.SAVING")}
              </>
            ) : (
              editingItem ? t("BUTTONS.UPDATE") : t("BUTTONS.SAVE")
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          backgroundColor: "rgba(255, 255, 255, 0.05)",
          borderRadius: "12px",
          padding: "4px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          maxWidth: "360px",
          margin: "0 auto 20px auto",
        }}
      >
        <button
          onClick={() => {
            // Limpa a rota de edição quando volta para a aba Cercas
            if (params.id) {
              navigate("/electric-fence");
            }
            // Limpa o estado de edição e visualização
            setEditingItem(null);
            setIsViewMode(false);
            setViewingItem(null);
            isUsingNewFlowRef.current = false; // Reseta a flag do novo fluxo
            setActiveTab("grid");
          }}
          style={{
            padding: "8px 20px",
            border: "none",
            borderRadius: "8px",
            backgroundColor:
              activeTab === "grid" ? "var(--theme-primary)" : "transparent",
            color: activeTab === "grid" ? "white" : "#6c757d",
            fontWeight: activeTab === "grid" ? "600" : "500",
            fontSize: "13px",
            cursor: "pointer",
            transition: "all 0.3s ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            boxShadow:
              activeTab === "grid"
                ? "0 2px 8px rgba(108, 92, 231, 0.3)"
                : "none",
            minWidth: "220px",
            whiteSpace: "nowrap",
          }}
        >
          <MdFence size={14} />
          {t("ELETRONIC_FENCES.TITLE")}
        </button>

        <button
          onClick={() => setActiveTab("map")}
          style={{
            padding: "8px 20px",
            border: "none",
            borderRadius: "8px",
            backgroundColor:
              activeTab === "map" ? "var(--theme-primary)" : "transparent",
            color: activeTab === "map" ? "white" : "#6c757d",
            fontWeight: activeTab === "map" ? "600" : "500",
            fontSize: "13px",
            cursor: "pointer",
            transition: "all 0.3s ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            boxShadow:
              activeTab === "map"
                ? "0 2px 8px rgba(108, 92, 231, 0.3)"
                : "none",
            minWidth: "120px",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
          </svg>
          {t("TITLE.MAP")}
        </button>
      </div>
      {activeTab === "map" && (
        <div className="position-relative w-100" style={{ height: "82vh" }}>
          <div
            className="position-absolute top-0 start-0 bg-white p-3 rounded-3 shadow modal-fences"
            style={{
              zIndex: 10,
              margin: "12px",
              marginTop: "90px",
              minWidth: "340px",
              maxWidth: "92vw",
            }}
          >
            <h5 style={{ marginBottom: 12 }}>
              {isViewMode && !editingItem
                ? t("ELETRONIC_FENCES.VIEW_FENCE")
                : editingItem
                  ? t("ELETRONIC_FENCES.EDIT_FENCE")
                  : t("ELETRONIC_FENCES.CREATE_FENCE")}
            </h5>

            {/* Seletor de origem/destino */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                {t("ELETRONIC_FENCES.CLICK_MAP_DEFINE")}
              </label>
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="mode"
                    value="origin"
                    defaultChecked
                    onChange={() => (modeRef.current = "origin")}
                    id="modeOrigin"
                    disabled={isViewMode && !editingItem}
                  />
                  <label className="form-check-label" htmlFor="modeOrigin">
                    {t("ELETRONIC_FENCES.ORIGIN")}
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="mode"
                    value="destination"
                    onChange={() => (modeRef.current = "destination")}
                    id="modeDestination"
                    disabled={isViewMode && !editingItem}
                  />
                  <label className="form-check-label" htmlFor="modeDestination">
                    {t("ELETRONIC_FENCES.DESTINATION")}
                  </label>
                </div>
              </div>
            </div>

            {/* Inputs de origem e destino */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                {t("ELETRONIC_FENCES.ORIGIN_INPUT_LABEL")}
              </label>
              <input
                ref={originInputRef}
                type="text"
                className="form-control"
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
                placeholder={t("ELETRONIC_FENCES.ORIGIN_PLACEHOLDER")}
                autoComplete="off"
                disabled={isViewMode && !editingItem}
              />
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">
                {t("ELETRONIC_FENCES.DESTINATION_INPUT_LABEL")}
              </label>
              <input
                ref={destinationInputRef}
                type="text"
                className="form-control"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                placeholder={t("ELETRONIC_FENCES.DESTINATION_PLACEHOLDER")}
                autoComplete="off"
                disabled={isViewMode && !editingItem}
              />
            </div>

            {/* Botões principais */}
            <div className="d-flex flex-wrap gap-2 mb-3">
              <button
                className="btn btn-primary btn-sm"
                onClick={routeNow}
                disabled={isViewMode && !editingItem}
              >
                {t("ELETRONIC_FENCES.CALCULATE_ROUTE")}
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={swapOriginDestination}
                disabled={isViewMode && !editingItem}
              >
                {t("ELETRONIC_FENCES.SWAP")}
              </button>
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={clearAll}
                disabled={isViewMode && !editingItem}
              >
                {t("ELETRONIC_FENCES.CLEAR")}
              </button>
            </div>

            {/* Buffer */}
            <div className="mb-3 border-top pt-3">
              <div className="d-flex align-items-end gap-2">
                <div className="flex-grow-1">
                  <label className="form-label fw-semibold">
                    {t("ELETRONIC_FENCES.BUFFER_WIDTH")}
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    min="10"
                    max="10000"
                    step="10"
                    value={bufferDistance}
                    onChange={(e) => setBufferDistance(Number(e.target.value))}
                    disabled={isViewMode && !editingItem}
                  />
                </div>
                <button
                  className="btn btn-outline-primary"
                  onClick={applyBuffer}
                  disabled={isViewMode && !editingItem}
                >
                  {t("ELETRONIC_FENCES.APPLY_BUFFER")}
                </button>
              </div>
            </div>

            {/* Legenda */}
            <div className="mt-3 d-flex align-items-center gap-4">
              <div className="d-flex align-items-center gap-2">
                <span
                  className="rounded-pill"
                  style={{ width: 18, height: 4, backgroundColor: "#1e40af" }}
                ></span>
                <small>{t("ELETRONIC_FENCES.ORIGINAL_ROUTE")}</small>
              </div>

              <div className="d-flex align-items-center gap-2">
                <span
                  className="rounded-pill"
                  style={{ width: 18, height: 4, backgroundColor: "#93c5fd" }}
                ></span>
                <small>{t("ELETRONIC_FENCES.ELECTRONIC_FENCE")}</small>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
                marginTop: 20,
              }}
            >
              {isViewMode && !editingItem && (
                <button
                  className="btn btn-primary w-100"
                  onClick={handleEditFromView}
                >
                  {t("RISK_AREA.EDIT")}
                </button>
              )}

              {(!isViewMode || editingItem) && (
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      clearAll();
                      setIsViewMode(false);
                      setViewingItem(null);
                      if (params.id) {
                        navigate("/electric-fence");
                      }
                    }}
                  >
                    {t("BUTTONS.CANCEL")}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      if (editingItem) {
                        // Se estiver editando, abre o modal com o nome preenchido
                        setShowModal(true);
                      } else {
                        // Se for novo, abre o modal vazio
                        setMarkerDescription("");
                        setShowModal(true);
                      }
                    }}
                  >
                    {editingItem ? t("BUTTONS.UPDATE") : t("BUTTONS.SAVE")}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mapa */}
          <div ref={mapRef} className="w-100 h-100"></div>

          {/* Toast de erro */}
          {showToast && (
            <ToastError
              message={t("ELETRONIC_FENCES.ERROR_CALCULATE_ROUTE")}
              onClose={() => setShowToast(false)}
            />
          )}
        </div>
      )}
      
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />
      {activeTab === "grid" && (
        <div style={{ position: "relative", minHeight: "400px" }}>
          {dataLoading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "var(--bg-card)",
                opacity: 0.95,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                borderRadius: "8px",
                backdropFilter: "blur(2px)",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <div
                  className="spinner-border"
                  role="status"
                  style={{
                    width: "3rem",
                    height: "3rem",
                    color: "var(--theme-primary)",
                    borderWidth: "0.25em",
                  }}
                >
                  <span className="visually-hidden">{t("COMMON.LOADING")}...</span>
                </div>
                <p
                  style={{
                    marginTop: "1rem",
                    color: "var(--text-primary)",
                    fontSize: "14px",
                    fontWeight: "500"
                  }}
                >
                  {t("ELETRONIC_FENCES.LOADING_FENCES")}
                </p>
              </div>
            </div>
          )}
          <GridComponent
            dataSource={data}
            allowPaging={true}
            height="auto"
            pageSettings={{ pageSize }}
          >
            <ColumnsDirective>
              <ColumnDirective
                field="markerDescription"
                headerText={t("ELETRONIC_FENCES.FENCE_NAME")}
                width="150"
                textAlign="Left"
                template={(props) => {
                  return (
                    <a
                      href={`https://maps.google.com/?q=${getLocationCoordinates(
                        "markerDescription",
                        props
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        cursor: "pointer",
                        textDecoration: "underline",
                        color: "inherit",
                        fontSize: "13px",
                      }}
                    >
                      <div>{props.markerDescription}</div>
                    </a>
                  );
                }}
              />
              <ColumnDirective
                field="radius"
                headerText={t("ELETRONIC_FENCES.RADIUS")}
                width="80"
                textAlign="Left"
              />
              <ColumnDirective
                headerText={t("ELETRONIC_FENCES.BIND_UNBIND")}
                width="80"
                textAlign="Center"
                template={(row) => (
                  <button
                    className="btn btn-link"
                    style={{ color: "var(--text-primary)" }}
                    onClick={() => {
                      setSelectedBindMarker(row);
                      setShowModalControl(true);
                    }}
                  >
                    <FaLink size={20} />
                  </button>
                )}
              />

              <ColumnDirective
                headerText={t("TITLE.MAP")}
                width="10%"
                textAlign="Center"
                template={(data) => (
                  <button
                    className="btn btn-link"
                    style={{ color: "#4285F4" }}
                    onClick={() =>
                      handleViewOnMap(data)
                    }
                    title={t("ELETRONIC_FENCES.VIEW_ON_MAP")}
                  >
                    <FaMapMarkerAlt color="red" size={20} />
                    </button>
                )}
              />

              <ColumnDirective
                headerText={t("RISK_AREA.EDIT")}
                width="80"
                textAlign="Center"
                template={(data) => (
                  <button
                    className="btn btn-link"
                    style={{ color: "var(--text-primary)" }}
                    onClick={() => handleParamEdit(data)}
                  >
                    <FaEdit size={18} />
                  </button>
                )}
              />

              <ColumnDirective
                headerText={t("BUTTONS.DELETE")}
                width="80"
                textAlign="Center"
                template={(data) => (
                  <button
                    className="btn btn-link"
                    style={{ color: "var(--text-primary)" }}
                    onClick={() => openDeleteModal(data)}
                  >
                    <FaTrash size={18} />
                  </button>
                )}
              />
            </ColumnsDirective>
            <Inject services={[Page]} />
          </GridComponent>
        </div>
      )}
    </>
  );
};
